import { Button } from '@/components/ui/button';
import FileUpload from '@/components/FileUpload/FileUpload';
import ShadCnFormInput from '@/components/FormComponents/ShadCnFormInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { PATH } from '@/constants';
import { CTA_ACTION_TYPE, CTA_TYPE, MAGNET_TYPE } from '@/constants/bot';
import { useUserContext } from '@/contexts/UserProvider';
import { useUploadVendorFile } from '@/hooks/useBot';
import { IPageCTAForm } from '@/types/page-cta';
import { handleShowError } from '@/utils';
import { cn } from '@/lib/utils';
import { MessageSquare, Users, Calendar, FileText, X, Loader2 } from 'lucide-react';
import Image from 'next/image';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

interface ICTAOptionItemProps {
  name: 'option_a' | 'option_b' | 'option_c';
}

/**
 * Flat action type that the vendor selects. Maps to the underlying
 * CTA_TYPE + CTA_ACTION_TYPE pair without exposing the two-level distinction.
 */
type FlatAction = 'open_chatbot' | 'connect_to_agent' | 'show_calendar' | 'lead_magnet';

const FLAT_ACTIONS: {
  value: FlatAction;
  label: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  color: string;
  bg: string;
}[] = [
  {
    value: 'open_chatbot',
    label: 'Open Chat',
    description: 'Opens the full chat window',
    icon: MessageSquare,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
  },
  {
    value: 'connect_to_agent',
    label: 'Connect to Agent',
    description: 'Initiates human handoff',
    icon: Users,
    color: 'text-green-600',
    bg: 'bg-green-50 border-green-200',
  },
  {
    value: 'show_calendar',
    label: 'Show Calendar',
    description: 'Opens booking calendar',
    icon: Calendar,
    color: 'text-orange-500',
    bg: 'bg-orange-50 border-orange-200',
  },
  {
    value: 'lead_magnet',
    label: 'Share a File',
    description: 'Gates a PDF, video, or image',
    icon: FileText,
    color: 'text-purple-600',
    bg: 'bg-purple-50 border-purple-200',
  },
];

/** Derive flat action from stored form values */
function getFlatAction(type: string, actionType: string): FlatAction | '' {
  if (type === CTA_TYPE.LEAD_MAGNET) return 'lead_magnet';
  if (actionType === CTA_ACTION_TYPE.CONNECT_TO_AGENT) return 'connect_to_agent';
  if (actionType === CTA_ACTION_TYPE.SHOW_CALENDAR) return 'show_calendar';
  if (actionType === CTA_ACTION_TYPE.OPEN_CHATBOT) return 'open_chatbot';
  return '';
}

const CTAOptionItem: React.FunctionComponent<ICTAOptionItemProps> = ({ name }) => {
  const { vendorConfig } = useUserContext();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);

  const form = useFormContext<IPageCTAForm>();
  const option = form.watch(name);
  const flatAction = getFlatAction(option?.type || '', option?.config?.action_type || '');

  const { mutate: uploadVendorFile, isPending: isUploading } = useUploadVendorFile();

  const getFileTypeFromUrl = (url: string): MAGNET_TYPE | null => {
    const extension = url.split('.').pop()?.toLowerCase();
    if (!extension) return null;
    if (['mp4', 'mkv', 'm4v', 'mov', 'avi', 'wmv', 'flv', 'webm'].includes(extension)) return MAGNET_TYPE.VIDEO;
    if (['png', 'jpeg', 'jpg', 'gif'].includes(extension)) return MAGNET_TYPE.IMAGE;
    if (extension === 'pdf') return MAGNET_TYPE.PDF;
    return null;
  };

  const handleSelectAction = (action: FlatAction) => {
    const needsIntegration = (
      action === 'connect_to_agent' || action === 'show_calendar'
    ) && !vendorConfig?.calendar_enabled;

    if (needsIntegration) {
      setOpenDialog(true);
      return;
    }

    if (action === 'lead_magnet') {
      form.setValue(`${name}.type`, CTA_TYPE.LEAD_MAGNET);
      form.setValue(`${name}.config.action_type`, '');
    } else {
      form.setValue(`${name}.type`, CTA_TYPE.ACTION);
      form.setValue(`${name}.config.action_type`, action as CTA_ACTION_TYPE);
    }
  };

  const handleFilesChange = (newFiles: File[]) => {
    const file = newFiles[0];
    if (!file) return;
    setFiles(newFiles);
    const fileType = getFileTypeFromUrl(file.name);
    uploadVendorFile(file, {
      onSuccess: (data) => {
        form.setValue(`${name}.config.url`, data.url);
        form.setValue(`${name}.config.magnet_type`, fileType || MAGNET_TYPE.VIDEO);
        toast.success('File uploaded successfully');
      },
      onError: handleShowError,
    });
  };

  const handleReset = () => {
    form.setValue(`${name}.type`, '');
    form.setValue(`${name}.label`, '');
    form.setValue(`${name}.config`, { action_type: '', url: '', magnet_type: '', file_id: '' });
    setFiles([]);
    setTimeout(() => form.trigger(name), 100);
  };

  const isEmpty = !option?.type && !option?.label && !option?.config?.url && !option?.config?.action_type;

  const optionLabel = name.split('_')[1].toUpperCase();

  return (
    <>
      <div className='rounded-lg border border-gray-200 bg-white overflow-hidden'>
        {/* Card header */}
        <div className='flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200'>
          <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
            Button {optionLabel}
          </span>
          {!isEmpty && (
            <button
              type='button'
              onClick={handleReset}
              className='flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors'
            >
              <X className='h-3 w-3' />
              Clear
            </button>
          )}
        </div>

        <div className='p-4 space-y-4'>
          {/* Step 1: Action type selector */}
          <div>
            <p className='text-xs font-medium text-gray-600 mb-2'>What does this button do?</p>
            <div className='grid grid-cols-2 gap-2'>
              {FLAT_ACTIONS.map((action) => {
                const Icon = action.icon;
                const isSelected = flatAction === action.value;
                return (
                  <button
                    key={action.value}
                    type='button'
                    onClick={() => handleSelectAction(action.value)}
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg border p-2.5 text-left transition-all',
                      isSelected
                        ? `${action.bg} ring-1 ring-inset ring-current`
                        : 'border-gray-200 bg-white hover:bg-gray-50',
                    )}
                  >
                    <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white shadow-sm', isSelected && action.color)}>
                      <Icon className='h-3.5 w-3.5' />
                    </span>
                    <span className='min-w-0'>
                      <span className={cn('block text-xs font-semibold', isSelected ? action.color : 'text-gray-700')}>
                        {action.label}
                      </span>
                      <span className='block text-[10px] text-gray-400 leading-tight truncate'>
                        {action.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Label — only show once action is chosen */}
          {flatAction && (
            <div>
              <label className='block text-xs font-medium text-gray-600 mb-1.5'>
                Button label
              </label>
              <ShadCnFormInput
                name={`${name}.label`}
                placeholder={
                  flatAction === 'open_chatbot' ? 'e.g. Ask a question'
                  : flatAction === 'connect_to_agent' ? 'e.g. Talk to Sales'
                  : flatAction === 'show_calendar' ? 'e.g. Book a Demo'
                  : 'e.g. Download our guide'
                }
              />
            </div>
          )}

          {/* Step 3: Lead magnet sub-fields */}
          {flatAction === 'lead_magnet' && (
            <div className='space-y-3'>
              <div>
                <label className='block text-xs font-medium text-gray-600 mb-1.5'>
                  Paste a URL (PDF, video, or image)
                </label>
                <ShadCnFormInput
                  name={`${name}.config.url`}
                  placeholder='https://example.com/guide.pdf'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const fileType = getFileTypeFromUrl(e.target.value);
                    if (fileType) form.setValue(`${name}.config.magnet_type`, fileType);
                  }}
                />
              </div>
              <div className='flex items-center gap-3'>
                <div className='h-px flex-1 bg-gray-200' />
                <span className='text-xs text-gray-400'>or upload</span>
                <div className='h-px flex-1 bg-gray-200' />
              </div>
              <FileUpload
                files={files}
                setFiles={handleFilesChange}
                type={['png', 'jpeg', 'jpg', 'gif', 'mp4', 'mkv', 'm4v', 'mov', 'avi', 'wmv', 'flv', 'webm', 'pdf']}
                customChildren={
                  <div className='border border-dashed border-gray-300 rounded-lg flex items-center justify-center py-3 text-xs text-gray-500 gap-2 hover:border-gray-400 hover:bg-gray-50 transition-colors cursor-pointer'>
                    {isUploading ? (
                      <><Loader2 className='h-4 w-4 animate-spin' /> Uploading…</>
                    ) : (
                      <>
                        <FileText className='h-4 w-4' />
                        {files[0]?.name || 'Choose a file to upload'}
                      </>
                    )}
                  </div>
                }
                hideFileList
              />
            </div>
          )}
        </div>
      </div>

      {/* Integration required dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className='flex flex-col items-center max-w-[555px] px-4 pt-7.5 pb-6 !rounded-xl'>
          <DialogHeader>
            <VisuallyHidden asChild>
              <DialogTitle>Action Requires Integration</DialogTitle>
            </VisuallyHidden>
          </DialogHeader>
          <Image
            src='/images/integrations-dialog-image.png'
            alt='Integrations dialog image'
            width={195}
            height={225}
          />
          <p className='text-base font-semibold text-gray-900 text-center mt-8'>
            Action Requires Integration
          </p>
          <p className='text-sm leading-relaxed text-gray-500 text-center'>
            To enable &quot;Connect to Agent&quot; or &quot;Show Calendar,&quot; please sync your
            calendar and Slack via Integrations.
          </p>
          <Button className='w-full mt-6' href={PATH.INTEGRATION}>
            Go to Integrations
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CTAOptionItem;
